/**
 * ActionCardsSection Component
 *
 * Container for the Vulnerability Detector card.
 *
 * Atomic level: Organism
 * Composes: ActionCard, VulnerabilityRecordRow
 * Used by: TodayPage
 */

import React from 'react';
import { ActionCard } from './ActionCard';
import { VulnerabilityRecordRow } from './VulnerabilityRecordRow';
import type { ActionCardsSectionProps } from '../types/today.types';
import { useNavigation } from '../../../../contexts/NavigationContext';

/**
 * Container for the Vulnerability Detector card.
 */
export function ActionCardsSection({
  vulnerabilityDetector,
}: ActionCardsSectionProps): React.ReactElement {
  const { navigateWithReferrer } = useNavigation();
  // Individual record clicks → v4 record detail, in-app, carrying a referrer
  // so "Back" returns to Today.
  const handleRecordClick = (id: string, collectionId: string) => {
    navigateWithReferrer(`/v4/records/${collectionId}/${id}`);
  };

  return (
    <div className="grid grid-cols-1 gap-4">
      {/* Vulnerability Detector Card */}
      <ActionCard
        title="Vulnerability Detector"
        total={vulnerabilityDetector.total}
        delta={vulnerabilityDetector.delta}
        trend={vulnerabilityDetector.trend}
      >
        {vulnerabilityDetector.topRecords.length > 0 ? (
          vulnerabilityDetector.topRecords.map((record) => (
            <VulnerabilityRecordRow key={record.id} record={record} onClick={handleRecordClick} />
          ))
        ) : (
          <EmptyState message="No vulnerability cases in the last 24 hours" />
        )}
      </ActionCard>
    </div>
  );
}

/**
 * Empty state component for cards with no records.
 */
function EmptyState({ message }: { message: string }): React.ReactElement {
  return (
    <div className="flex items-center justify-center px-6 py-8 text-[12px] text-muted-foreground">
      {message}
    </div>
  );
}
